import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CreditCard, Landmark, TrendingUp, ArrowRight, Lock, Smartphone } from "lucide-react";
import { api, formatDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PAYMENT_TYPES = [
  { id: "deposit",    icon: <Landmark size={22}/>,   title: "Deposit",             subtitle: "15% one-time deposit to open your property" },
  { id: "instalment", icon: <CreditCard size={22}/>, title: "Monthly Instalment",  subtitle: "Your monthly property payment" },
  { id: "investor",   icon: <TrendingUp size={22}/>, title: "Investor Contribution", subtitle: "Certified-investor programme — figures shared privately after eligibility check" },
];

// Suggested defaults per (type, currency)
const SUGGESTED = {
  deposit:    { gbp: 15000, eur: 18000, usd: 19500, zar: 300000, ngn: 25000000, kes: 2500000, ghs: 200000, ugx: 65000000, zmw: 400000, tzs: 45000000 },
  instalment: { gbp: 800,   eur: 950,   usd: 1000,  zar: 16000,  ngn: 1300000,  kes: 130000,  ghs: 11000,  ugx: 3500000,  zmw: 22000,  tzs: 2400000 },
  investor:   { gbp: 25000, eur: 30000, usd: 30000, zar: 500000, ngn: 40000000, kes: 4000000, ghs: 350000, ugx: 100000000,zmw: 700000, tzs: 75000000 },
};

const EUROPE_CURRENCIES = [
  { code: "gbp", label: "GBP £", gateway: "stripe" },
  { code: "eur", label: "EUR €", gateway: "stripe" },
  { code: "usd", label: "USD $", gateway: "both" },
];

const AFRICA_CURRENCIES = [
  { code: "zar", label: "ZAR R",  gateway: "flw" },
  { code: "ngn", label: "NGN ₦",  gateway: "flw" },
  { code: "kes", label: "KES KSh", gateway: "flw" },
  { code: "ghs", label: "GHS ₵",  gateway: "flw" },
  { code: "ugx", label: "UGX",    gateway: "flw" },
  { code: "zmw", label: "ZMW K",  gateway: "flw" },
  { code: "tzs", label: "TZS",    gateway: "flw" },
];

const STRIPE_METHODS  = ["Card", "SEPA", "iDEAL", "Bancontact", "Klarna"];
const FLW_METHODS     = ["Card", "M-Pesa", "MTN Momo", "Airtel Money", "USSD", "Bank Transfer", "EFT"];

// Currencies Stripe supports in this app
const STRIPE_OK = new Set(["gbp", "eur", "usd"]);
const FLW_OK    = new Set(["ngn", "kes", "ghs", "zar", "ugx", "zmw", "tzs", "usd"]);

export default function PayPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const params = new URLSearchParams(window.location.search);
  const initialType = params.get("type") || "deposit";

  const [type, setType] = useState(initialType);
  const [currency, setCurrency] = useState("gbp");
  const [gateway, setGateway] = useState("stripe"); // "stripe" | "flw"
  const [amount, setAmount] = useState(SUGGESTED[initialType]?.gbp ?? SUGGESTED.deposit.gbp);

  const [email, setEmail] = useState(user?.email || "");
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);

  // Suggested gateway when currency changes
  const suggestGateway = (cur) => {
    if (STRIPE_OK.has(cur) && !FLW_OK.has(cur)) return "stripe";
    if (FLW_OK.has(cur)  && !STRIPE_OK.has(cur)) return "flw";
    // USD supports both — default to stripe
    return cur === "zar" || cur === "ngn" || cur === "kes" || cur === "ghs" ? "flw" : "stripe";
  };

  const changeCurrency = (cur) => {
    setCurrency(cur);
    const g = suggestGateway(cur);
    setGateway(g);
    const suggested = SUGGESTED[type]?.[cur] || SUGGESTED[type]?.gbp || 0;
    setAmount(suggested);
  };

  const changeType = (id) => {
    setType(id);
    const suggested = SUGGESTED[id]?.[currency] || SUGGESTED[id]?.gbp || 0;
    setAmount(suggested);
  };

  const gatewayValid = useMemo(() => {
    if (gateway === "stripe") return STRIPE_OK.has(currency);
    if (gateway === "flw")    return FLW_OK.has(currency);
    return false;
  }, [gateway, currency]);

  const startCheckout = async () => {
    if (!amount || amount <= 0) { toast.error("Please enter a valid amount."); return; }
    if (!gatewayValid) { toast.error(`Selected gateway doesn't support ${currency.toUpperCase()}.`); return; }

    setLoading(true);
    try {
      if (gateway === "stripe") {
        const { data } = await api.post("/payments/checkout", {
          payment_type: type,
          amount: parseFloat(amount),
          currency: currency.toLowerCase(),
          origin_url: window.location.origin,
        });
        if (data.url) window.location.href = data.url;
      } else {
        if (!email || !name) {
          toast.error("Name and email are required for Flutterwave.");
          setLoading(false);
          return;
        }
        const { data } = await api.post("/payments/flutterwave/checkout", {
          payment_type: type,
          amount: parseFloat(amount),
          currency: currency.toLowerCase(),
          origin_url: window.location.origin,
          email, name, phone,
        });
        if (data.url) window.location.href = data.url;
      }
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const activeMethods = gateway === "stripe" ? STRIPE_METHODS : FLW_METHODS;

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-16 md:py-20" data-testid="pay-page">
      <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>§ Secure Payments</span></div>
      <h1 className="font-display text-3xl md:text-4xl lg:text-5xl mv-green-text mt-3 leading-tight">
        Make a payment — <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>safe, fast, protected.</span>
      </h1>
      <p className="mt-4 text-[15px] max-w-2xl" style={{ color: "var(--mv-muted)" }}>
        Powered by <b className="mv-green-text">Stripe</b> (Europe / cards / SEPA) and <b className="mv-green-text">Flutterwave</b> (Africa / Mobile Money / M-Pesa). Every payment is tracked and mirrored to your Personal Log.
      </p>

      <div className="mt-6 mv-card p-4 flex items-start gap-3 border" style={{ background: "linear-gradient(180deg, #fff, rgba(233,60,60,0.05))", borderColor: "rgba(179,58,58,0.35)" }} data-testid="scam-banner-pay">
        <span className="text-[13.5px]">
          <b style={{ color: "#B33A3A" }}>Scam warning:</b> Never pay MAVULA via WhatsApp, SMS, DMs, gift cards, or crypto wallets.
          Payments only happen on this page. <a href="/scam-warning" className="underline font-semibold mv-green-text">Read the full warning →</a>
        </span>
      </div>

      {/* Payment type */}
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-4">
        {PAYMENT_TYPES.map((p) => {
          const active = p.id === type;
          return (
            <button
              key={p.id}
              onClick={() => changeType(p.id)}
              data-testid={`pay-type-${p.id}`}
              className={`text-left mv-card p-6 transition-all duration-300 ${active ? "ring-2 ring-[color:var(--mv-gold)]" : "hover:-translate-y-1"}`}
            >
              <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-4"
                   style={{ background: active ? "var(--mv-green)" : "rgba(212,175,55,0.16)", color: active ? "#fff" : "var(--mv-green)" }}>
                {p.icon}
              </div>
              <div className="font-display text-xl mv-green-text">{p.title}</div>
              <div className="text-[13.5px] mt-1" style={{ color: "var(--mv-muted)" }}>{p.subtitle}</div>
            </button>
          );
        })}
      </div>

      {/* Gateway + form */}
      <div className="mt-6 mv-card p-6 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Left column */}
        <div>
          {/* Gateway toggle */}
          <div>
            <span className="text-[12.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Payment Gateway</span>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <button
                onClick={() => setGateway("stripe")}
                data-testid="gateway-stripe"
                className={`px-4 py-3 rounded-2xl border text-left transition-colors ${gateway === "stripe" ? "mv-green-bg text-white border-transparent" : "bg-white mv-hairline hover:bg-black/5"}`}
              >
                <div className="font-display text-[15px]">Stripe</div>
                <div className={`text-[11.5px] ${gateway === "stripe" ? "text-white/80" : ""}`} style={gateway === "stripe" ? {} : { color: "var(--mv-muted)" }}>
                  Europe · Cards · SEPA · iDEAL
                </div>
              </button>
              <button
                onClick={() => setGateway("flw")}
                data-testid="gateway-flw"
                className={`px-4 py-3 rounded-2xl border text-left transition-colors ${gateway === "flw" ? "mv-green-bg text-white border-transparent" : "bg-white mv-hairline hover:bg-black/5"}`}
              >
                <div className="font-display text-[15px] flex items-center gap-1.5"><Smartphone size={14}/> Flutterwave</div>
                <div className={`text-[11.5px] ${gateway === "flw" ? "text-white/80" : ""}`} style={gateway === "flw" ? {} : { color: "var(--mv-muted)" }}>
                  Africa · M-Pesa · Momo · USSD
                </div>
              </button>
            </div>
          </div>

          {/* Currencies */}
          <div className="mt-6">
            <span className="text-[12.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Currency — Europe / Global</span>
            <div className="mt-2 flex flex-wrap gap-2">
              {EUROPE_CURRENCIES.map((c) => (
                <button
                  key={c.code}
                  onClick={() => changeCurrency(c.code)}
                  data-testid={`pay-currency-${c.code}`}
                  className={`px-3 py-1.5 rounded-full text-[12.5px] font-medium border transition-colors duration-200 ${currency === c.code ? "mv-green-bg text-white border-transparent" : "bg-white mv-hairline hover:bg-black/5"}`}
                >{c.label}</button>
              ))}
            </div>

            <span className="mt-4 block text-[12.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Currency — Africa</span>
            <div className="mt-2 flex flex-wrap gap-2">
              {AFRICA_CURRENCIES.map((c) => (
                <button
                  key={c.code}
                  onClick={() => changeCurrency(c.code)}
                  data-testid={`pay-currency-${c.code}`}
                  className={`px-3 py-1.5 rounded-full text-[12.5px] font-medium border transition-colors duration-200 ${currency === c.code ? "mv-green-bg text-white border-transparent" : "bg-white mv-hairline hover:bg-black/5"}`}
                >{c.label}</button>
              ))}
            </div>
          </div>

          {/* Amount */}
          <label className="block mt-6">
            <span className="text-[12.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Amount</span>
            <input
              type="number"
              min={1}
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="pay-amount-input"
              className="mt-2 w-full px-4 py-2.5 rounded-xl border mv-hairline text-[15px]"
            />
          </label>

          {/* Buyer (Flutterwave requires) */}
          {gateway === "flw" && (
            <div className="mt-6 space-y-3" data-testid="flw-buyer-form">
              <label className="block">
                <span className="text-[12.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Full Name</span>
                <input value={name} onChange={(e) => setName(e.target.value)} data-testid="flw-name" className="mt-1 w-full px-4 py-2.5 rounded-xl border mv-hairline"/>
              </label>
              <label className="block">
                <span className="text-[12.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Email</span>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="flw-email" className="mt-1 w-full px-4 py-2.5 rounded-xl border mv-hairline"/>
              </label>
              <label className="block">
                <span className="text-[12.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Phone (with country code)</span>
                <input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="flw-phone" placeholder="+254 …" className="mt-1 w-full px-4 py-2.5 rounded-xl border mv-hairline"/>
              </label>
            </div>
          )}

          <div className="mt-6">
            <span className="text-[12.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Methods at checkout</span>
            <div className="mt-2 flex flex-wrap gap-2">
              {activeMethods.map((m) => (
                <span key={m} className="text-[12px] px-2.5 py-1 rounded-full mv-hairline border bg-white" style={{ color: "var(--mv-muted)" }}>{m}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Right column — summary */}
        <div className="rounded-2xl p-6 flex flex-col justify-between" style={{ background: "linear-gradient(135deg, rgba(10,59,40,0.05), rgba(212,175,55,0.08))" }}>
          <div>
            <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>Summary</span></div>
            <div className="mt-3 font-display text-2xl mv-green-text">{PAYMENT_TYPES.find(p=>p.id===type)?.title}</div>
            <div className="text-[13.5px] mt-1" style={{ color: "var(--mv-muted)" }}>via <b className="mv-green-text">{gateway === "stripe" ? "Stripe" : "Flutterwave"}</b> · {currency.toUpperCase()}</div>
            <div className="mt-6 font-display text-[40px] leading-none mv-green-text tracking-tight">
              {new Intl.NumberFormat("en-GB", { style: "currency", currency: currency.toUpperCase(), maximumFractionDigits: 0 }).format(amount || 0)}
            </div>
            <div className="mt-3 text-[13px] flex items-center gap-1.5" style={{ color: "var(--mv-muted)" }}>
              <Lock size={13}/> {gateway === "stripe" ? "Secured by Stripe · PCI compliant" : "Secured by Flutterwave · Pan-African"}
            </div>
            {!gatewayValid && (
              <div className="mt-3 text-[12.5px] p-2.5 rounded-lg" style={{ background: "rgba(212,175,55,0.16)", color: "#8a6b12" }}>
                This gateway doesn't accept {currency.toUpperCase()}. Please switch gateway or currency.
              </div>
            )}
          </div>
          <button
            onClick={startCheckout}
            disabled={loading || !gatewayValid}
            className="btn-gold mt-8 w-full justify-center disabled:opacity-60"
            data-testid="pay-checkout-btn"
          >
            {loading ? "Redirecting…" : (<>Continue to Secure Checkout <ArrowRight size={16}/></>)}
          </button>
          <button onClick={() => navigate("/dashboard")} className="text-[13px] mt-3 mv-green-text underline">
            Back to Personal Log
          </button>
        </div>
      </div>
    </div>
  );
}
